// components/masterdata/TypenListe.jsx

import React, {useState, useEffect, useCallback} from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router';
import Navbar from '../utils/Navbar';

const TypenListe = () => {
  const { fetchWithAuth, accessToken } = useAuth();
  const navigate = useNavigate();

  const [typen, setTypen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [newTyp, setNewTyp] = useState({
    name: "",
    order: ""
  });

  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});

  const fetchTypen = useCallback(async () => {
    try {
      const response = await fetchWithAuth(`/api/data/typen`, {
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error('Fehler beim Laden der Typen');
      }

      const data = await response.json();
      setTypen(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  const handleSave = async () => {
    try {
      for (const id in editData) {
        await fetchWithAuth(`/api/data/typen/${id}/`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(editData[id]),
        });
      }
      setEditMode(false);
      fetchTypen();
    } catch (err) {
      alert("Fehler beim Speichern: " + err.message);
    }
  };

  const handleDeleteSingle = async (id) => {
    if (!window.confirm("Willst du diesen Typ wirklich löschen?")) return;

    try {
      await fetchWithAuth(`/api/data/typen/${id}/`, {
        method: "DELETE",
      });
      fetchTypen(); // Liste neu laden
    } catch (err) {
      alert("Fehler beim Löschen: " + err.message);
    }
  };

  const handleAddTypen = async () => {
    if (!newTyp.name.trim()) return;

    try {
      const response = await fetchWithAuth(`/api/data/typen/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newTyp)
      });

      if (response.ok) {
        setNewTyp({ name: "", name_abk: "", order: "" });
        fetchTypen();
      } else {
        alert('Fehler beim Hinzufügen');
      }
    } catch (err) {
      alert('Netzwerkfehler: ' + err.message);
    }
  };

  useEffect(() => {
    if (!accessToken) return;
    fetchTypen();
  }, [accessToken, fetchTypen]);

  if (loading) return <div>Lade Typen...</div>;
  if (error) return <div className="text-danger">Fehler: {error}</div>;

  return (
    <>
      <Navbar />
      <div className="container mt-4">
        <h2 className="mb-4">Typen verwalten</h2>

        <div className="d-flex mb-4 gap-2">
          <input
            className="form-control"
            type="text"
            placeholder="Neuer Typ..."
            value={newTyp.name}
            onChange={(e) => setNewTyp({ ...newTyp, name: e.target.value })}
          />
          <input
            className="form-control"
            type="text"
            placeholder="Sortierung"
            value={newTyp.order}
            onChange={(e) => setNewTyp({ ...newTyp, order: e.target.value })}
          />
          <button className="btn btn-success" onClick={handleAddTypen} disabled={editMode}>
            Hinzufügen
          </button>
        </div>

        {editMode && (
          <div className="alert alert-warning d-flex align-items-center" role="alert">
            <i className="bi bi-pencil-square me-2"></i>
            Bearbeitungsmodus aktiv – Änderungen noch nicht gespeichert!
          </div>
        )}

        <table className="table table-striped">
          <thead>
            <tr>
              {editMode && <th>Aktion</th>}
              <th>ID</th>
              <th>Name</th>
              <th>Sortierung</th>
            </tr>
          </thead>
          <tbody>
            {typen.map((kat) => (
              <tr key={kat.id}>
                {editMode && (
                  <td>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDeleteSingle(kat.id)}
                    >
                      🗑️
                    </button>
                  </td>
                )}
                <td>{kat.id}</td>

                {/* Name */}
                <td>
                  {editMode ? (
                    <input
                      type="text"
                      className="form-control"
                      value={editData[kat.id]?.name || ""}
                      onChange={(e) =>
                        setEditData({...editData, [kat.id]: { ...editData[kat.id], name: e.target.value }
                        })
                      }
                    />
                  ) : (
                    kat.name
                  )}
                </td>

                {/* Order */}
                <td>
                  {editMode ? (
                    <input
                      type="number"
                      className="form-control"
                      value={editData[kat.id]?.order || ""}
                      onChange={(e) =>
                        setEditData({...editData, [kat.id]: { ...editData[kat.id], order: e.target.value }
                        })
                      }
                    />
                  ) : (
                    kat.order
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="d-flex flex-wrap gap-2 mb-4">
          {editMode ? (
            <button
              className="btn btn-warning"
              onClick={() => {
                setEditMode(false);
                setEditData({});
              }}
            >
              Abbrechen
            </button>
          ) : (
            <button
              className="btn btn-secondary"
              onClick={() => navigate("/objekte")}
            >
              Zurück
            </button>
          )}

          {!editMode && (
            <button className="btn btn-primary" onClick={() => {
              setEditMode(true);
              const initialData = {};
              typen.forEach(k => {
                initialData[k.id] = {
                  name: k.name,
                  order: k.order
                };
              });
              setEditData(initialData);
            }}>
              Bearbeiten
            </button>
          )}

          {editMode && (
              <button className="btn btn-success" onClick={handleSave}>
                Änderungen speichern
              </button>
          )}
        </div>
      </div>
    </>
  );
};

export default TypenListe;
